// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Crafts
//  ---------------------------------------------------------------------------
//  Everything this device can run, in one place.
//
//  ── WHY A LIBRARY AND NOT A DAY ───────────────────────────────────────────
//  A manifestation is not something that happened on Tuesday. Five dashboards
//  dropped on a project board would be five cards in today's writing if they
//  were records, which is the failure the media epic already named about
//  reference material and did not get to fix. So they live here, and a board
//  portal is a REFERENCE to one rather than the thing itself — which is also
//  what lets the same manifestation sit on two boards at once.
//
//  ── WHAT IT SAYS ABOUT WHAT IT CANNOT DO ──────────────────────────────────
//  The bytes are device-local (md-file.ts), so a manifestation built on a laptop
//  genuinely is not on the phone. The row says so rather than offering an Open
//  that would open nothing. Same posture as FileFrame: three states, and the
//  forbidden one is the blank rectangle with no explanation.
//
//  Deleting takes the row, the grant and the rows the manifestation was keeping.
//  Not the BYTES: those are content-addressed and may be shared with another
//  instance of the same document, so sweeping them is a separate question about
//  orphans — the one M-10 already exists for.
// ════════════════════════════════════════════════════════════════════════════

const { useState: useStateML, useEffect: useEffectML, useLayoutEffect: useLayoutEffectML,
  useMemo: useMemoML, useRef: useRefML } = React;
const { ink: inkML, mdRgba: rgbaML, useMdDismissLayer: useEscapeML, MDI: MDIML, mdIsLight: isLightML } = window;

/* One row's action. Not a shared component from elsewhere: the sheets in this
   app each build their own controls out of the same grammar rather than through
   a widget layer, and adding one now for three buttons would be inventing an
   abstraction to serve its first use. */
function LibButton({ label, onClick, light, danger }) {
  return (
    <button onClick={onClick}
      style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 9, cursor: 'pointer',
        fontFamily: 'var(--md-body)', fontSize: 12, fontWeight: 600,
        color: danger ? '#e0574d' : inkML(0.7),
        border: `0.75px solid ${danger ? 'rgba(224,87,77,0.32)' : inkML(light ? 0.12 : 0.18)}`,
        background: light ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.05)' }}>
      {label}
    </button>);
}

function CraftGlyph({ color = 'currentColor', size = 24 }) {
  return <svg aria-hidden="true" focusable="false" data-md-glyph="craft" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" style={{ width: size, height: size, minWidth: size, minHeight: size,
      flex: `0 0 ${size}px` }}>
    <path d="M12 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-6" />
    <path d="M19 2c0 2 1.2 3.2 3.2 3.2-2 0-3.2 1.2-3.2 3.2 0-2-1.2-3.2-3.2-3.2 2 0 3.2-1.2 3.2-3.2z" fill={color} stroke="none" />
  </svg>;
}

/* Availability belongs to the device, not to one mount of this screen. Keeping
   the answer here means a panel handoff cannot briefly turn a known-missing
   source back into an apparently runnable Craft while IndexedDB answers again. */
const craftAvailability = new Map();

function readCraftRows() {
  return window.mdCraftList ? window.mdCraftList() : [];
}

function knownCraftAvailability(rows) {
  return Object.fromEntries(rows.flatMap((row) => {
    const known = craftAvailability.get(row.instance);
    return known && known.file === row.file ? [[row.instance, known.present]] : [];
  }));
}

function CraftSpace({ accent, onClose, onPlace, onView, initial = null, active = true,
  phoneDocument = false }) {
  const light = isLightML();
  /* The catalogue is synchronous by contract. Reading it in the initializer is
     what makes the FIRST Crafts paint contain the Crafts: the old mount effect
     painted the empty-state sentence first, then replaced it one frame later. */
  const [rows, setRows] = useStateML(readCraftRows);
  const [query, setQuery] = useStateML('');
  const [renaming, setRenaming] = useStateML(null);
  const [draft, setDraft] = useStateML('');
  const [here, setHere] = useStateML(() => knownCraftAvailability(rows));
  const [expanded, setExpanded] = useStateML(initial);
  const [notice, setNotice] = useStateML('');
  const wasActive = useRefML(active);

  const refresh = () => setRows(readCraftRows());

  /* Staying mounted must not mean staying stale. A Craft can be created from a
     desk while this screen is hidden, so re-read the synchronous catalogue on
     the hidden → visible edge. Layout effect makes the new list part of the
     returning screen's first paint instead of adding it a frame afterwards. */
  useLayoutEffectML(() => {
    if (active && !wasActive.current) refresh();
    wasActive.current = active;
  }, [active]);

  /* A deep link can change while this same persistent panel is hidden. Expand
     the requested Craft without keying (and therefore destroying) the screen. */
  useEffectML(() => { setExpanded(initial || null); }, [initial]);

  /* Which of these this device actually holds the bytes for. Asked once when the
     sheet opens rather than per row per render — it is an IndexedDB round trip
     each, and a list that re-queried on every keystroke of the search box would
     be doing real work to answer a question that cannot have changed. */
  useEffectML(() => {
    if (!window.mdFileGet) return undefined;
    let alive = true;
    const missing = rows.filter((row) => {
      const known = craftAvailability.get(row.instance);
      return !known || known.file !== row.file;
    });
    if (!missing.length) {
      setHere(knownCraftAvailability(rows));
      return () => { alive = false; };
    }
    Promise.all(missing.map((r) => window.mdFileGet(r.file).then((b) => [r.instance, !!b])))
      .then((pairs) => {
        for (const [instance, present] of pairs) {
          const row = rows.find((item) => item.instance === instance);
          if (row) craftAvailability.set(instance, { file: row.file, present });
        }
        if (alive) setHere(knownCraftAvailability(rows));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [rows]);

  const shown = useMemoML(() => {
    const q = query.trim().toLowerCase();
    const list = rows.slice().sort((a, b) => (b.created || 0) - (a.created || 0));
    return q ? list.filter((r) => r.name.toLowerCase().includes(q)) : list;
  }, [rows, query]);

  useEscapeML(onClose, active);

  const commitRename = (row) => {
    const name = draft.trim().slice(0, window.MD_CRAFT_NAME_MAX || 60);
    if (name && name !== row.name) window.mdCraftPut({ ...row, name });
    setRenaming(null);
    refresh();
  };

  const remove = (row) => {
    window.mdCraftRemove(row.instance);
    if (window.mdGrantRevoke) window.mdGrantRevoke(row.instance);
    if (window.mdMStoreDelete) window.mdMStoreDelete(row.instance);
    if (window.mdCraftActivityClear) window.mdCraftActivityClear(row.instance);
    craftAvailability.delete(row.instance);
    refresh();
  };

  const grantOf = (row) => (window.mdGrantRead ? window.mdGrantRead(row.instance).level : 'none');

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const safeName = (name) => String(name || 'craft').replace(/[^a-z0-9._-]+/gi, '-');

  const exportSource = async (row) => {
    const blob = await window.mdFileGet(row.file);
    if (!blob) { setNotice('The source is not on this device.'); return; }
    download(blob, `${safeName(row.name)}.html`);
  };

  const exportPackage = async (row) => {
    const blob = await window.mdFileGet(row.file);
    if (!blob) { setNotice('The source is not on this device.'); return; }
    const privateData = window.mdMStoreGet ? await window.mdMStoreGet(row.instance) : null;
    const payload = { format: 'mandana-craft', version: 1, exported: Date.now(),
      craft: { name: row.name, source: await blob.text(), privateData } };
    download(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
      `${safeName(row.name)}.craft.json`);
  };

  const replaceSource = async (row, file) => {
    if (!file) return;
    if (file.size > (window.MD_CRAFT_MAX_BYTES || 512 * 1024)) {
      setNotice('That Craft is too large to open here.'); return;
    }
    const raw = await file.text().catch(() => '');
    const source = window.mdCraftSource(raw);
    if (!source) { setNotice('That file is not a runnable Craft.'); return; }
    const htmlFile = file.type === 'text/html' ? file
      : new File([raw], file.name || 'craft.html', { type: 'text/html' });
    const put = await window.mdFilePut(htmlFile);
    if (!put.ok || !window.mdCraftReplace(row, put.ref.id, source.name || row.name,
      source.capabilities || [], undefined, source.declarations)) {
      setNotice('The update could not be stored on this device.'); return;
    }
    if (window.mdGrantRevoke) window.mdGrantRevoke(row.instance);
    window.dispatchEvent(new CustomEvent('md:craft-permissions', {
      detail: { instance: row.instance, level: 'none' },
    }));
    setNotice('Updated. Its desk placement and private data were kept; journal access was reset.');
    refresh();
  };

  const rollback = (row) => {
    const next = window.mdCraftRollback && window.mdCraftRollback(row, 0);
    if (!next) return;
    if (window.mdGrantRevoke) window.mdGrantRevoke(row.instance);
    window.dispatchEvent(new CustomEvent('md:craft-permissions', {
      detail: { instance: row.instance, level: 'none' },
    }));
    setNotice('Restored the previous source. Journal access was reset.');
    refresh();
  };

  const importPackage = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setNotice('That Craft package is too large to import.'); return;
    }
    let payload = null;
    try { payload = JSON.parse(await file.text()); } catch (_e) { payload = null; }
    const current = payload && payload.format === 'mandana-craft' && payload.version === 1
      ? payload.craft : null;
    const legacy = payload && payload.format === 'mandana-manifest' && payload.version === 1
      ? payload.manifest : null;
    const item = current || legacy;
    const source = item && window.mdCraftSource(item.source);
    if (!source) { setNotice('That is not a ManDana Craft package.'); return; }
    const html = new File([item.source], `${safeName(item.name)}.html`, { type: 'text/html' });
    const put = await window.mdFilePut(html);
    if (!put.ok) { setNotice('The package could not be stored on this device.'); return; }
    const instance = window.mdCraftId();
    const row = { instance, file: put.ref.id, name: String(item.name || source.name || 'Craft').slice(0, 60),
      created: Date.now(), revision: 1, capabilities: source.capabilities || [], declarations: source.declarations };
    if (!window.mdCraftPut(row)) { setNotice('The package could not be added.'); return; }
    if (item.privateData !== undefined && item.privateData !== null && window.mdMStorePut) {
      await window.mdMStorePut(instance, item.privateData);
    }
    setNotice('Imported as a new Craft. Permissions were not carried over.');
    refresh();
  };

  return (
    /* The material and its content are deliberately still. index.html makes
       this the same atomic handoff used by every screen inside the panel. */
    <div data-md-panel-screen="atomic" data-md-crafts-active={active ? 'true' : 'false'}
      data-md-phone-document-screen={phoneDocument && active ? 'true' : undefined}
      aria-hidden={active ? undefined : 'true'} inert={active ? undefined : ''}
      style={{ position: 'absolute', inset: 0, zIndex: 90, display: 'flex', flexDirection: 'column',
      ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
      borderRadius: 'var(--md-radius-sheet) var(--md-radius-sheet) 0 0',
      visibility: active ? 'visible' : 'hidden', pointerEvents: active ? 'auto' : 'none' }}>

      {/* Always flush to the screen — absolute inset 0 at z-index 90, with
          nothing around it that could have paid on its behalf — so it pays for
          the top itself. 18px is the rest it already had; the device only ever
          raises it. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
        padding: 'max(var(--md-safe-top), 18px) 18px 8px', flexShrink: 0 }}>
        <CraftGlyph color={rgbaML(accent, 1)} size={22} />
        <span style={{ flex: 1, color: inkML(1), fontSize: 19, fontWeight: 680 }}>Crafts</span>
        <label style={{ color: inkML(0.58), fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
          padding: '6px 8px', fontFamily: 'var(--md-body)' }}>
          Import
          <input type="file" accept="application/json,.json" style={{ display: 'none' }}
            onChange={(e) => { importPackage(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </label>
        <window.MdDismiss onClick={onClose} label="Close Crafts"
          style={{ marginInlineEnd: -10 }} />
      </div>

      {/* The foot is a claim too: the last manifestation in the list must be
          scrollable clear of the home indicator, not under it. */}
      <div data-md-phone-document-content={phoneDocument && active ? 'true' : undefined}
        className="md-scroll" style={{ flex: 1, overflowY: 'auto',
        padding: '6px 16px max(var(--md-safe-bottom), 32px)' }}>
      <input value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Search Crafts" aria-label="Search Crafts"
        style={{ width: '100%', padding: '10px 12px', borderRadius: 12, marginBottom: 12,
          border: `0.75px solid ${inkML(light ? 0.12 : 0.18)}`, fontFamily: 'var(--md-body)',
          background: light ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.05)',
          color: inkML(0.9), fontSize: 14, outline: 'none' }} />

      {notice && <div role="status" style={{ margin: '0 2px 10px', color: inkML(0.58),
        fontSize: 12.5, lineHeight: 1.5, fontFamily: 'var(--md-body)' }}>{notice}</div>}

      {shown.length === 0 && (
        <p style={{ color: inkML(0.45), fontSize: 13.5, lineHeight: 1.6, margin: '18px 2px',
          fontFamily: 'var(--md-body)' }}>
          {rows.length === 0
            ? 'Nothing here yet. Drop an .html file onto a desk, or paste the HTML straight onto it, and it becomes a Craft — a document that runs, sealed off from everything except what you allow it.'
            : 'Nothing matches that.'}
        </p>)}

      {shown.map((row) => {
        const checked = Object.prototype.hasOwnProperty.call(here, row.instance);
        const present = here[row.instance] === true;
        const level = grantOf(row);
        return (
          <div key={row.instance}
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '11px 2px',
              borderBottom: `0.75px solid ${inkML(light ? 0.07 : 0.1)}` }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              {renaming === row.instance ? (
                <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitRename(row)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitRename(row);
                    if (e.key === 'Escape') setRenaming(null); }}
                  aria-label="Name"
                  style={{ width: '100%', padding: '5px 8px', borderRadius: 9, fontSize: 14,
                    fontFamily: 'var(--md-body)', color: inkML(0.95),
                    border: `0.75px solid ${rgbaML(accent, 0.5)}`,
                    background: light ? '#fff' : 'rgba(255,255,255,0.06)', outline: 'none' }} />
              ) : (
                <span style={{ display: 'block', color: inkML(present ? 0.9 : 0.45), fontSize: 14,
                  fontWeight: 600, fontFamily: 'var(--md-body)', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
              )}
              <span style={{ display: 'block', color: inkML(0.4), fontSize: 11.5, marginTop: 3,
                fontFamily: 'var(--md-body)' }}>
                {!checked ? 'Checking this device…'
                  : !present ? 'On the device it was added to'
                  : level === 'write' ? 'May read and change your records'
                  : level === 'read' ? 'May read your records'
                  : 'Reaches nothing of yours'}{row.revision > 1 ? ` · revision ${row.revision}` : ''}
              </span>
            </span>

            {present && onPlace && (
              <LibButton label="Add to desk" light={light}
                onClick={() => { onPlace(row); onClose(); }} />)}
            {present && (
              <LibButton label={expanded === row.instance ? 'Less' : 'More'} light={light}
                onClick={() => {
                  const next = expanded === row.instance ? null : row.instance;
                  if (next && onView) onView(row);
                  setExpanded(next);
                }} />)}
            <LibButton label="Delete" light={light} danger onClick={() => remove(row)} />
            {expanded === row.instance && <div style={{ flexBasis: '100%', display: 'flex',
              flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '5px 0 3px 12px' }}>
              <LibButton label="Rename" light={light}
                onClick={() => { setDraft(row.name); setRenaming(row.instance); }} />
              <label style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 9, cursor: 'pointer',
                fontFamily: 'var(--md-body)', fontSize: 12, fontWeight: 600, color: inkML(0.7),
                border: `0.75px solid ${inkML(light ? 0.12 : 0.18)}`,
                background: light ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.05)' }}>
                Update source
                <input type="file" accept="text/html,.html" style={{ display: 'none' }}
                  onChange={(e) => { replaceSource(row, e.target.files && e.target.files[0]); e.target.value = ''; }} />
              </label>
              <LibButton label="Export HTML" light={light} onClick={() => exportSource(row)} />
              <LibButton label="Export package" light={light} onClick={() => exportPackage(row)} />
              {row.history && row.history.length > 0 &&
                <LibButton label="Restore previous" light={light} onClick={() => rollback(row)} />}
              <span style={{ flexBasis: '100%', color: inkML(0.38), fontSize: 11.5,
                fontFamily: 'var(--md-body)', lineHeight: 1.5 }}>
                {(row.capabilities || []).length
                  ? `Uses ${row.capabilities.join(', ')}`
                  : 'No journal capabilities declared'}
                {window.mdCraftActivityList && window.mdCraftActivityList(row.instance).length
                  ? ` · ${window.mdCraftActivityList(row.instance).length} recent actions` : ''}
              </span>
              {window.mdCraftActivityList && window.mdCraftActivityList(row.instance).slice(0, 3)
                .map((activity, index) => <span key={`${activity.at}:${index}`}
                  style={{ flexBasis: '100%', display: 'flex', gap: 8, color: inkML(0.48),
                    fontSize: 11.5, fontFamily: 'var(--md-body)', lineHeight: 1.45 }}>
                  <time style={{ flexShrink: 0, color: inkML(0.3) }}>
                    {new Date(activity.at).toLocaleString([], { month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit' })}
                  </time>
                  <span>{activity.summary || activity.op || 'Changed ManDana'}</span>
                </span>)}
            </div>}
          </div>);
      })}
      </div>
    </div>);
}

window.CraftSpace = CraftSpace;
